import { CameraOutlined } from "@ant-design/icons";
import { message } from "antd";
import { staffApi } from "api/staff.api";
import { BAvatar, BButton, BInput, BModal } from "components/base";
import { observer } from "mobx-react";
import {
  type ChangeEvent,
  type FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { userStore } from "store/userStore";
import { $urlImage } from "utils/url";
import "./EditProfileModal.scss";

type EditProfileModalProps = {
  open: boolean;
  onClose: () => void;
};

const getInitials = (name: string) => {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
};

const EditProfileModal = observer(function EditProfileModal({
  open,
  onClose,
}: EditProfileModalProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [displayName, setDisplayName] = useState("");
  const [username, setUsername] = useState("");
  const [avatarPath, setAvatarPath] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }

    setDisplayName(userStore.info.name ?? "");
    setUsername(userStore.info.username ?? "");
    setAvatarPath(userStore.info.avatar ?? "");
  }, [open]);

  const avatarSrc = useMemo(
    () => (avatarPath ? $urlImage(avatarPath) : undefined),
    [avatarPath],
  );

  const initials = useMemo(() => {
    const fallbackName = displayName || username || "Người dùng";
    return getInitials(fallbackName);
  }, [displayName, username]);

  const handleDisplayNameChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setDisplayName(event.target.value);
    },
    [],
  );

  const handleAvatarButtonClick = useCallback(() => {
    if (uploading || saving) {
      return;
    }
    fileInputRef.current?.click();
  }, [saving, uploading]);

  const handleAvatarChange = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = "";

      if (!file) {
        return;
      }

      setUploading(true);
      try {
        const res = await staffApi.upload(file);
        const path = res.data?.path as string | undefined;

        if (!path) {
          message.error("Tải ảnh thất bại");
          return;
        }

        setAvatarPath(path);
      } catch {
        // Error toast handled by request interceptor
      } finally {
        setUploading(false);
      }
    },
    [],
  );

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      const nextName = displayName.trim();

      if (!nextName) {
        message.error("Tên hiển thị không được để trống");
        return;
      }

      setSaving(true);
      try {
        await userStore.updateProfile({
          staff: {
            name: nextName,
            avatar: avatarPath || undefined,
          },
        });
        message.success("Cập nhật hồ sơ thành công");
        onClose();
      } finally {
        setSaving(false);
      }
    },
    [avatarPath, displayName, onClose],
  );

  return (
    <BModal
      open={open}
      title="Chỉnh sửa hồ sơ"
      footer={null}
      onCancel={onClose}
      width={420}
      className="edit-profile-modal"
      destroyOnHidden
    >
      <form className="edit-profile-modal__form" onSubmit={handleSubmit}>
        <div className="edit-profile-modal__avatar-wrap">
          <BAvatar className="edit-profile-modal__avatar" src={avatarSrc}>
            {initials}
          </BAvatar>
          <BButton
            type="default"
            htmlType="button"
            className="edit-profile-modal__camera"
            icon={<CameraOutlined />}
            onClick={handleAvatarButtonClick}
            loading={uploading}
            disabled={uploading || saving}
            aria-label="Tải ảnh đại diện"
          />
          <input
            ref={fileInputRef}
            className="edit-profile-modal__file"
            type="file"
            accept="image/*"
            onChange={handleAvatarChange}
          />
        </div>

        <label className="edit-profile-modal__field">
          <span className="edit-profile-modal__label">Tên hiển thị</span>
          <BInput
            value={displayName}
            onChange={handleDisplayNameChange}
            autoComplete="name"
          />
        </label>

        <label className="edit-profile-modal__field edit-profile-modal__field--disabled">
          <span className="edit-profile-modal__label">Tên đăng nhập</span>
          {username}
        </label>

        <p className="edit-profile-modal__hint">
          Hồ sơ giúp mọi người nhận ra bạn trong các cuộc trò chuyện nhóm.
        </p>

        <div className="edit-profile-modal__actions">
          <BButton
            htmlType="button"
            className="edit-profile-modal__cancel"
            onClick={onClose}
            disabled={saving || uploading}
          >
            Hủy
          </BButton>
          <BButton
            htmlType="submit"
            type="primary"
            className="edit-profile-modal__save"
            loading={saving}
            disabled={uploading}
          >
            Lưu
          </BButton>
        </div>
      </form>
    </BModal>
  );
});

export default EditProfileModal;
